const ttl = 2 * 60 * 1000;
let cache = {}; // cache for full config map requests
let cachedCountry = ''; // this is the country for which the config has been loaded.

/**
 * Checks to see if the date is expired if we aren't in a browser
 * @param {number} expireTime
 * @return {boolean}
 * @private
 */
const _isExpired = (expireTime) => {
    // In the browser(if sessionStorage exists) we don't want to clear the cache because
    // it is already cleared on every page load.
    // The browser relies on getCachedConfiguration to function properly.
    // Mobile and lambda can still function because they wont be calling getCachedConfiguration
    // directly like the web does
    return typeof sessionStorage === "undefined" && expireTime < Date.now();
}

/**
 * Resets cache if a new country is being used.
 * @param {string} country
 */
const checkCountry = (country) => {
    cache = cachedCountry !== country ? {} : cache;
    cachedCountry = country;
}

/**
 * Get a value from the cache
 * @param {string} key - The key of the value to retrieve
 * @returns {*} The cached value or undefined if not found or expired
 */
const get = (key) => {
    const item = cache[key];
    if (item && !_isExpired(item.expiresAt)) {
        return item.value;
    }
    // Remove the expired item
    delete cache[key];
    return undefined;
};

/**
 * Check if a key exists in the cache and has not expired
 * @param {string} key - The key to check
 * @returns {boolean} true if the key exists in the cache and has not expired, false otherwise
 */
const has = (key) => {
    const item = cache[key];
    return item && !_isExpired(item.expiresAt);
};

/**
 * Set a value in the cache with a TTL (time-to-live) in minutes
 * @param {string} key - The key of the value to set
 * @param {*} value - The value to cache
 * @param {number} ttl - The time-to-live duration in minutes
 */
const set = (key, value) => {
    const expiresAt = Date.now() + ttl;
    cache[key] = { value, expiresAt };
};

/**
 * Takes a list of configs and sets them in the cache for the country
 * @param {Object} configs
 */
const updateCache = (configs) => {
    Object.keys(configs).forEach((key) => {
        set(key, {...configs[key]});
    });
}

/**
 * Clear the entire cache
 */
const clear = () => {
    cachedCountry = '';
    cache = {};
};

/**
 * Get an array of all keys in the cache
 * @returns {string[]} An array of keys in the cache
 */
const keys = () => {
    return Object.keys(cache);
};

/**
 * Checks to make sure configs are in cache.
 * If not throws an error so the application can make sure the configurations are loaded first
 * @param {String[]} configNames
 */
const checkConfigCache = (configNames = []) => {
    configNames.forEach((configName) => {
        if (!has(configName)) {
            throw new Error(`${configName} configCache has not been loaded yet!
            This needs to be done prior to getting it from cache.`);
        }
    });
}

module.exports = {
    get,
    set,
    clear,
    keys,
    updateCache,
    checkCountry,
    checkConfigCache
}
